import { getBrandTitle, getNyrisBrandConfig, NyrisBrandConfig } from '../../config/brand-config';
import { AGROPARTS_PRODUCT_LINK_TEMPLATE } from '../../configuration';

const NYRIS_SCRIPT_URL = 'https://assets.nyris.io/nyris-widget/v1/widget.js'
const NYRIS_MOUNT_POINT = 'nyris-mount-point';
export const NYRIS_TRIGGER_ID = 'nyris-visual-search-initiator';

interface NyrisSettings {
    initiatorElementId: string[];
    navigatePreference: string;
    apiKey: string;
    customerLogo: string;
    primaryColor: string;
    cameraIconColour: string;
    browseGalleryButtonColor: string;
    logoWidth: number;
    language: string;
    ctaButtonText: string;
    productLinkBaseURL: string;
}

declare global {
    interface Window {
        nyrisSettings?: NyrisSettings;
        nyrisWidgetLoaded?: boolean;
        loadWidget?: () => void;
    }
}

const nyrisLangMap: Record<string, string> = {
    sv: 'se', // Swedish (Sweden) should be 'se'
};

const ctaTexts: Record<string, string> = {
    en: "More info",
    no: "Mer informasjon",
    da: "Mere information",
    sv: "Mer information",
    fr: "Plus d'infos",
    nl: "Meer info",
    "nl-be": "Meer info",
    "fr-be": "Plus d'infos",
    "en-ca": "More info",
    "fr-ca": "Plus d'infos",
    de: "Mehr Informationen",
    it: "Ulteriori informazioni",
    es: "Más info",
    hu: "További információ",
    pl: "Więcej informacji",
    ru: "Дополнительная информация",
};

/**
 * Prepares and initializes the Nyris visual search widget.
 * Sets up DOM elements, configures settings, and injects the widget script.
 * Only runs in browser environments.
 */
export function prepareNyrisWidget() {
    if (typeof window === 'undefined') {
        return;
    }
    const langFull = (localStorage.getItem('lang') || 'en-ww').toLowerCase();
    const langBase = langFull.split('-')[0];
    // For 'be' and 'ca', use the full language string if present
    let ctaText = ctaTexts[langFull];
    if (!ctaText) {
        ctaText = ctaTexts[langBase] || ctaTexts['en'];
    }

    // Nyris language setting
    let nyrisLanguage = nyrisLangMap[langFull];
    if (!nyrisLanguage) {
        nyrisLanguage = nyrisLangMap[langBase] || langBase;
    }

    // Ensure trigger button exists
    try {
        if (!document.getElementById(NYRIS_TRIGGER_ID)) {
            const btn = document.createElement('button');
            btn.id = NYRIS_TRIGGER_ID;
            btn.style.display = 'none';
            document.body.appendChild(btn);
        }
    } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
            console.error('Error creating Nyris trigger button:', error);
        }
        return;
    }

    // Ensure mount point exists
    try {
        if (!document.getElementById(NYRIS_MOUNT_POINT)) {
            const div = document.createElement('div');
            div.id = NYRIS_MOUNT_POINT;
            document.body.appendChild(div);
        }
    } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
            console.error('Error creating Nyris mount point:', error);
        }
        return;
    }

    // Set nyrisSettings
    const config: NyrisBrandConfig = getNyrisBrandConfig();

    // Validate config
    if (!config || !config.apiKey) {
        if (process.env.NODE_ENV !== 'production') {
            console.error('Invalid Nyris config for widget helper:', config);
        }
        return;
    }

    const productLinkBaseURL = AGROPARTS_PRODUCT_LINK_TEMPLATE
        .replace(/{BRAND}/g, encodeURIComponent(getBrandTitle(false)))
        .replace(/{LANGUAGE}/g, encodeURIComponent(nyrisLanguage));

    if (!window.nyrisSettings) {
        window.nyrisSettings = {
            initiatorElementId: [NYRIS_TRIGGER_ID],
            navigatePreference: '_blank',
            apiKey: config.apiKey,
            customerLogo: config.customerLogo,
            primaryColor: config.primaryColor,
            cameraIconColour: config.cameraIconColor,
            browseGalleryButtonColor: config.browseGalleryButtonColor,
            logoWidth: config.logoWidth,
            language: nyrisLanguage,
            ctaButtonText: ctaText,
            productLinkBaseURL: productLinkBaseURL
        };
    }

    // Inject script and call loadWidget
    if (!document.querySelector('script[src*="nyris-widget"]')) {
        try {
            const script = document.createElement('script');
            script.src = NYRIS_SCRIPT_URL;
            script.async = true;
            script.onload = () => {
                window.nyrisWidgetLoaded = true;
                if (window.loadWidget) {
                    window.loadWidget();
                }
            };
            script.onerror = (error) => {
                if (process.env.NODE_ENV !== 'production') {
                    console.error('Failed to load Nyris widget script:', error);
                }
            };

            const initiatorIds = window.nyrisSettings?.initiatorElementId;
            const hasTriggerButton = Array.isArray(initiatorIds)
                ? initiatorIds.some((id: string) => document.getElementById(id))
                : !!document.getElementById(initiatorIds);

            if (!hasTriggerButton) {
                return;
            } else {
                document.body.appendChild(script);
            }
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('Error during Nyris widget script injection:', error);
            }
        }
    }
}